import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
//SERVICES
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { DataService } from 'src/app/services/data.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { MenuRegenerationService } from 'src/app/services/menu-regeneration.service';
import { WhiteLabelService } from 'src/app/services/white-label.service';
import { AuthService } from 'src/app/services/auth.service';
import { BrowserNavigationService } from 'src/app/services/browser-navigation.service';
import {
  MultisessionAccount,
  MultisessionAccountsByLetter,
} from './multisession-account.model';

enum authModals{ login = 'login', logout = 'logout'};

interface MultisessionModalCloseEvent {
  logged?: boolean;
  logout?: boolean;
  sessions?: MultisessionAccount[];
}

@Component({
  selector: 'app-multisession-sidebar',
  templateUrl: './multisession-sidebar.component.html',
  styleUrls: ['./multisession-sidebar.component.scss']
})
export class MultisessionSidebarComponent implements OnInit {
  @Input() hideTrigger = false;
  @ViewChild('loginModal', { static: true }) loginModal: ElementRef;
  @ViewChild('logoutModal', { static: true }) logoutModal: ElementRef;

  alphabet: string[] = ['a','b','c','d','e','f','g','h','i','j','k','l','m','n','ñ','o','p','q','r','s','t','u','v','w','x','y','z'];
  otherGroupKey = '#';
  filterBy: string[] = [];
  sort = 'desc';
  sessions: MultisessionAccount[] = [];
  orderedSessions: MultisessionAccountsByLetter = {};
  currentSession: MultisessionAccount;
  sessionSelected: MultisessionAccount;
  showSideBar = false;
  slideIn = false;
  modalRef: NgbModalRef;
  search: string | null = null;
  checkboxControl: Record<string, boolean> = {};

  constructor(
    private toastr: ToastrService,
    private translateService: TranslateService,
    private modalService: NgbModal,
    private dataService: DataService,
    private navigationService: NavigationService,
    private menuRegenerationService: MenuRegenerationService,
    private whiteLabelService: WhiteLabelService,
    private authService: AuthService,
    private router: Router,
    private browserNavigationService: BrowserNavigationService
  ){
    this.sessions = (this.dataService.getUserInMultisession() ?? []) as MultisessionAccount[];
    this.currentSession = this.dataService.getUser() ?? {};
  }

  ngOnInit(): void {
    this.groupAccountsByLetter();
    this.initCheckbox();
  }

  initCheckbox(){
    this.alphabet.map((l)=> this.checkboxControl[l] = false);
  }

  async changeCurrentUser(user: MultisessionAccount, preserveRoute = true){
    // VDS-3315 — both must be read/resolved BEFORE dataService.setUser() rewrites
    // localStorage.currentUser (design.md D5). router.url is read first, before the
    // syncFirebaseTokenIfSessionMatches await boundary.
    const previousUrl = this.router.url;
    const targetUrl = preserveRoute
      ? this.navigationService.resolvePostSwitchRoute(previousUrl, user)
      : this.navigationService.getTargetRouteForUser(user);

    this.currentSession = user;

    const switched = await this.authService.activateAccountWithGrant(user);
    if (!switched) {
      // Do not activate the snapshot without a matching Firebase identity:
      // that yields admin UI + proprietor Fb-Token (or vice versa) after reload.
      this.toastr.error(
        this.translateService.instant('auth.sessionMismatch.subtitle'),
      );
      return;
    }
    this.sessions = (this.dataService.getUserInMultisession() ?? []) as MultisessionAccount[];
    this.groupAccountsByLetter();

    this.toastr.success(this.translateService.instant('toastrMessage.success.accountChanged'));

    const building = user.buildings?.[0];
    if (building) {
      this.whiteLabelService.setSessionLoginPathFromBuilding(building);
      this.whiteLabelService.applyFromBuilding(building);
    }

    this.menuRegenerationService.triggerMenuRegeneration();
    this.changeView();

    // Full document load, always — it is what guarantees no per-building in-memory
    // state survives (design.md D4). The app is hash-routed, so a different target
    // is written to the fragment first; the reload then loads the document at it.
    // Changing only the fragment would be a same-document navigation, which is why
    // reload() is unconditional rather than an else-branch.
    if (targetUrl !== previousUrl) {
      this.browserNavigationService.setHashUrl(targetUrl);
    }
    this.browserNavigationService.reload();
  }

  check(letter: string){
    let foundIn = this.filterBy.findIndex((l:string) => l == letter);
    if(foundIn == -1){
      this.filterBy.push(letter);
      this.filterBy.sort((a,b)=> a.localeCompare(b));
    }else{
      this.filterBy.splice(foundIn, 1);
    }
  }

  // Nombre del PH de la sesion: es el identificador principal de la fila
  getBuildingName(session: MultisessionAccount): string{
    const buildingName = session?.buildings?.[0]?.name;
    return typeof buildingName === 'string' ? buildingName.trim() : '';
  }

  getPrimaryLabel(session: MultisessionAccount): string{
    return this.getBuildingName(session) || (session?.name ?? '');
  }

  // Solo hay linea secundaria cuando la principal ya muestra el PH
  getSecondaryLabel(session: MultisessionAccount): string{
    return this.getBuildingName(session) ? (session?.name ?? '') : '';
  }

  onSearchChange(){
    this.groupAccountsByLetter();
  }

  matchesSearch(session: MultisessionAccount): boolean{
    const needle = (this.search ?? '').trim().toLowerCase();
    if(!needle) return true;
    return [this.getBuildingName(session), session?.name ?? '']
      .some((value: string) => value.toLowerCase().includes(needle));
  }

  // Agrupa por la inicial del texto principal para que los encabezados A-Z sean coherentes.
  // Las iniciales acentuadas caen en su letra base y el resto (numeros, simbolos) en '#',
  // asi ninguna cuenta desaparece de la lista.
  groupKeyForLabel(label: string): string{
    const first = (label ?? '').trim().charAt(0).toLowerCase();
    if(!first) return this.otherGroupKey;
    if(this.alphabet.includes(first)) return first;
    const normalized = first.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return this.alphabet.includes(normalized) ? normalized : this.otherGroupKey;
  }

  groupAccountsByLetter(){
    const groups: MultisessionAccountsByLetter = {};
    for(const s of this.sessions){
      if(!this.matchesSearch(s)) continue;
      const key = this.groupKeyForLabel(this.getPrimaryLabel(s));
      groups[key] ? groups[key].push(s) : groups[key] = [s];
    }
    Object.keys(groups).forEach((key: string) =>
      groups[key].sort((a, b) => this.getPrimaryLabel(a).localeCompare(this.getPrimaryLabel(b)))
    );
    this.orderedSessions = {...groups};
  }

  openModal(modalClass: string, session?: MultisessionAccount): void {
    if(session) this.sessionSelected = session;
    const showModal = modalClass === authModals.login ? this.loginModal : this.logoutModal;
    this.modalRef = this.modalService.open(showModal, {
      windowClass: modalClass + '-modal',
      backdropClass: 'blur-backdrop',
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'md',
    });
  }

  closeModal(e: MultisessionModalCloseEvent): void {
    this.modalRef.close();
    if(e.logged){
      this.changeView();
      const nextSession = this.dataService.getUserInMultisession().pop();
      if (nextSession) {
        this.changeCurrentUser(nextSession as MultisessionAccount, false);
      }
    }
    if(e.logout){
      this.sessions = e.sessions ?? [];
      localStorage.setItem('usersSessions', JSON.stringify(e.sessions));
      this.groupAccountsByLetter();
      this.toastr.success(this.translateService.instant('toastrMessage.success.accountClosed'));
    }
  }

  changeView(){
    switch (this.slideIn) {
      case true:
        this.slideIn = false;
        setTimeout(() => { this.showSideBar = false }, 500);
      break;
      case false:
        this.showSideBar = true;
        setTimeout(() => { this.slideIn = true }, 100);
      break;
    }
  }

  restoreFilters(){
    this.filterBy = [];
    this.initCheckbox();
  }
}
